import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "about", label: "About" },
  { id: "listings", label: "Listings" },
  { id: "team", label: "Team" },
  { id: "blog", label: "Blog" },
  { id: "contact", label: "Contact" },
];

const Navigation = ({ alwaysDark = false }) => {
  const [scrolled, setScrolled] = useState(alwaysDark);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (alwaysDark) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [alwaysDark]);

  const handleNav = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      // We're not on the homepage — route there and scroll once mounted
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const baseText = scrolled ? "text-black" : "text-white";
  const baseBg = scrolled
    ? "bg-white/90 backdrop-blur-md border-b border-black/5"
    : "bg-transparent";

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${baseBg}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center h-20 md:h-24 gap-12">
        {/* Logo */}
        <button
          onClick={() => {
            if (location.pathname !== "/") {
              navigate("/");
            } else {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          data-testid="logo-button"
          aria-label="Manzano Real Estate"
          className="relative h-14 md:h-16 w-auto flex items-center shrink-0"
        >
          <img
            src="/assets/logo.png"
            alt="Manzano Real Estate"
            className={`h-full w-auto object-contain transition-opacity duration-500 invert ${
              scrolled ? "opacity-0" : "opacity-100"
            }`}
          />
          <img
            src="/assets/logo.png"
            alt="Manzano Real Estate"
            className={`absolute inset-0 h-full w-auto object-contain transition-opacity duration-500 ${
              scrolled ? "opacity-100" : "opacity-0"
            }`}
          />
        </button>

        {/* Desktop links — left-aligned next to logo */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => handleNav(link.id)}
              className={`uppercase tracking-[0.18em] font-medium ${baseText} transition-all duration-300 hover:opacity-60 relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-current hover:after:w-full after:transition-all after:duration-300`}
              style={{ fontSize: "16px" }}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Mobile menu trigger */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          className={`lg:hidden ml-auto ${baseText}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-[80vh] border-t border-black/10" : "max-h-0"
        } bg-white`}
      >
        <nav className="flex flex-col px-6 py-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`mobile-nav-link-${link.id}`}
              onClick={() => handleNav(link.id)}
              className="text-left py-4 text-sm uppercase tracking-[0.22em] text-black border-b border-black/5 last:border-0 hover:opacity-60 transition-opacity"
            >
              {link.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
